import type { NodeMap, EdgeMap, Node2dExtracted, Face2d } from "."
import { separateMeshes } from "./separateMeshes"
import type { Point2d, RoundaboutWay } from "../2d"
import {
  isSamePoint,
  sortNodesXY,
  findAngle,
  findOffsetAngle,
  nameShape,
} from "../2d"
import { overlaps } from "../utils"

const useSortNeighborsByAngle =
  (nodes: NodeMap, edges: EdgeMap) =>
  (node: Node2dExtracted, baseAngle = 0, direction?: RoundaboutWay) =>
    node.edgeIds
      .map((edgeId): [number, Node2dExtracted] => {
        const edge = edges.get(edgeId)
        if (!edge) throw new Error(`${edgeId} not found`)
        const neighborId = node.id === edge.node1id ? edge.node2id : edge.node1id
        const neighbor = nodes.get(neighborId)
        if (!neighbor) throw new Error(`${neighborId} not found`)
        const angle = findOffsetAngle(node, neighbor, baseAngle, direction)

        return [angle, neighbor]
      })
      .sort(([a], [b]) => a - b)
      .map(([_, neighbor]) => neighbor)

const useIsNodeExtreme = (nodesByXY: Node2dExtracted[]) => {
  return (node: Node2dExtracted, solvedNodeIds: Set<string>) => {
    const filter = (n: Node2dExtracted) => !solvedNodeIds.has(n.id)
    const nodesByYX = [...nodesByXY].sort((a, b) => a.y - b.y)
    const filtered = {
      xy: nodesByXY.filter(filter),
      yx: nodesByYX.filter(filter),
    }
    const extremes = [
      filtered.xy[0],
      filtered.xy[filtered.xy.length - 1],
      filtered.yx[0],
      filtered.yx[filtered.yx.length - 1],
    ]

    return extremes.some((extreme) => isSamePoint(extreme, node))
  }
}

export type DrawFacesProcedure = Point2d[][]

export type DrawFaces = (
  nodes: NodeMap,
  edges: EdgeMap,
) => { faces: Map<string, Face2d>; procedure: DrawFacesProcedure }

export const drawFaces: DrawFaces = (nodes, edges) => {
  const procedure: Point2d[][] = []
  const nodesByXY = sortNodesXY(Array.from(nodes.values()))
  const sortNeighborsByAngle = useSortNeighborsByAngle(nodes, edges)
  const isNodeExtreme = useIsNodeExtreme(nodesByXY)

  const solvedFaces: Map<string, Face2d> = new Map()
  const solvedNodeIds: Set<string> = new Set()

  const tryToSolve = (nodeIds: string[]) => {
    for (const nodeId of nodeIds) {
      const node = nodes.get(nodeId)
      const nodeBelongsToSingleFace =
        node?.edgeIds.length === 2 && isNodeExtreme(node, solvedNodeIds)
      if (nodeBelongsToSingleFace) solvedNodeIds.add(nodeId)
      if (nodesByXY[0].id === nodeId) {
        let faceCount = 0
        const solvedFaceIds = Array.from(solvedFaces.keys())
        for (const id of solvedFaceIds) {
          if (id.includes(nodeId)) faceCount++
        }
        if (node?.edgeIds.length === faceCount + 1) {
          solvedNodeIds.add(nodeId)
        }
      }
    }
  }
  // if (!nodesByXY[0].id.startsWith(`VIRTUAL`)) debugger
  for (const node of nodesByXY) {
    // if (node.id === `LbvERge-`) debugger
    if (solvedNodeIds.has(node.id)) continue
    let procedureStep: Point2d[] = []
    procedureStep.push(node)

    const draftFaces: Face2d[] = []

    const tryToAddFace = (...nodeIds: string[]) => {
      const draft = { nodeIds }
      const allPointsUnsolved = !overlaps(draft.nodeIds, solvedNodeIds)
      if (allPointsUnsolved) {
        draftFaces.push(draft)
      }
    }

    const neighborIds = sortNeighborsByAngle(node, Math.PI, `antigraphwise`).map(
      (neighbor) => neighbor.id,
    )

    for (let a = 0, b = 1; b < neighborIds.length; a++, b++) {
      tryToAddFace(neighborIds[a], node.id, neighborIds[b])
    }
    for (const draftFace of draftFaces) {
      if (overlaps(draftFace.nodeIds, solvedNodeIds)) continue
      let maxCycles = 20
      let faceSolved = false
      const { nodeIds } = draftFace
      while (!faceSolved && --maxCycles) {
        const headId = nodeIds[0]
        const tailId = nodeIds[nodeIds.length - 1]
        const neckId = nodeIds[1]
        const head = nodes.get(headId)
        const neck = nodes.get(neckId)
        if (!head || !neck) throw new Error(`${headId} or ${neckId} not found`)
        const angleFromHeadToNeck = findAngle(head, neck)
        const neighborsByAngle = sortNeighborsByAngle(head, angleFromHeadToNeck)
        const nextHead = neighborsByAngle[1]
        if (nextHead?.id === tailId) {
          const faceId = nameShape(...draftFace.nodeIds)
          solvedFaces.set(faceId, draftFace)
          tryToSolve(nodeIds)
          faceSolved = true
          continue
        }
        if (!nextHead) break
        if (solvedNodeIds.has(nextHead.id)) break
        nodeIds.unshift(nextHead.id)
        procedureStep = nodeIds.map((id) => nodes.get(id) ?? { x: 0, y: 0 })
      }
      procedure.push(procedureStep)
    }
  }

  return { faces: solvedFaces, procedure }
}

export const traceMeshes: DrawFaces = (nodes, edges) => {
  const meshes = separateMeshes({ nodes, edges })
  const outputs = meshes.map((mesh) => drawFaces(mesh.nodes, mesh.edges))

  return {
    faces: new Map(outputs.flatMap((output) => Array.from(output.faces))),
    procedure: outputs.flatMap((output) => output.procedure),
  }
}
